/**
 * InFlight — ADR-167 D1: one screen for what this fleet is doing right now.
 *
 * WHAT THIS REPLACES. On 2026-08-25 an operator watching a Fleet Architect turn saw
 * "working on it — 1016s elapsed". core-advisor was running concurrently and that was
 * learned only from a sentence an agent wrote in a chat reply. A Deploy Fleet run was
 * mid-flight at the same time, which is why a canary produced pre-fix output from
 * post-fix code and cost an hour of diagnosis. All three facts were already in the
 * backend and none had anywhere to be seen.
 *
 * THREE THINGS THIS SCREEN MUST NOT DO, each a defect this product has shipped once:
 *
 *  - render "nothing in flight" out of "we could not read it" (D3). That is
 *    RefChecksStatus degrading a 403 to "no checks", which showed as "waiting for them
 *    to start" over a red PR, forever, behind a 5-second poll.
 *  - go blank because one source failed (D4). The PR page has already been killed by a
 *    single GitHub read, and this reads several sources.
 *  - present a row with no move (D8). A list of problems with no actions is a console.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { ScopingCard, type AuthoringRunState, type Checkpoint } from '../components/AuthoringRail'
import { useAttentionNotify } from '../context/AttentionContext'

export interface InFlightRow {
  card_id?: string
  board?: string
  waiting_event_id?: number
  can_control?: boolean
  case_owner?: string
  allowed_actions?: string[]
  kind: string
  subject: string
  detail?: string
  state: string
  age_s: number
  beat_age_s?: number
  stale?: boolean
  owner?: string
  href?: string
  action?: string
  warn?: string
  reason?: string
  /** ADR-200 D6 — a run PARKED ON A PERSON: its brief waits to be confirmed or amended. */
  waiting_for?: string
  unattended?: boolean
  /** Whether this reader may confirm the brief right here. */
  decidable?: boolean
  run_agent?: string
  run_workflow?: string
  run_operator?: string
}

export interface InFlightResult {
  rows: InFlightRow[]
  /** COUNTED by the server, not derived from rows.length — see D3. */
  working: number
  /** ADR-200 D6 — runs waiting for a person, counted apart from `working`. */
  waiting?: number
  unreadable: number
  not_configured: number
  stale_after_s: number
  observed_at: string
}

export function parkedRowKey(row: InFlightRow): string {
  return `${row.run_agent ?? ''}/${row.run_workflow ?? ''}:${row.run_operator ?? row.owner ?? ''}:${row.waiting_for}`
}

export function isParkedBrief(row: Pick<InFlightRow, 'waiting_for'>): boolean {
  return row.waiting_for === 'brief_confirmation' || row.waiting_for === 'brief_amendment'
}

const isQueued = (row: Partial<Pick<InFlightRow, 'state'>>) => row.state === 'todo' || row.state === 'ready'

/** The backend's name for the parked-run source (in_flight.go: inFlightSource "authoring runs waiting for a person"); when it fails the response carries an unreadable row with this subject instead of its rows. */
export const PARKED_RUNS_SOURCE = 'authoring runs waiting for a person'

export function parkedSourceUnreadable(res: InFlightResult): boolean {
  return res.rows.some(row => row.kind === 'unreadable' && row.subject === PARKED_RUNS_SOURCE)
}

export function newlyParkedRows(
  seen: Set<string> | null,
  next: InFlightResult,
): { seen: Set<string> | null; alerts: InFlightRow[] } {
  if (parkedSourceUnreadable(next)) return { seen, alerts: [] }

  const parked = new Set(next.rows.filter(isParkedBrief).map(parkedRowKey))
  if (seen === null) return { seen: parked, alerts: [] }

  return {
    seen: parked,
    alerts: next.rows.filter(row => row.decidable && isParkedBrief(row) && !seen.has(parkedRowKey(row))),
  }
}

/** "18m", "4s" — short, because it is the SECONDARY field (D2). */
export function shortAge(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  const h = Math.floor(seconds / 3600)
  return `${h}h${Math.floor((seconds % 3600) / 60)}m`
}

/**
 * The sentence shown when no work is listed. THE WHOLE POINT OF D3 IS HERE.
 *
 * Exported and pure so the distinction is unit-testable without rendering: "nothing is
 * running" and "we could not tell" must never be the same string, and a screen that
 * assembles several sources is where they are most tempting to collapse.
 */
export function emptyStateSentence(
  r: Pick<InFlightResult, 'working' | 'unreadable' | 'not_configured'> & { waiting?: number; rows?: InFlightRow[] },
): string {
  if (r.working > 0) return ''
  // ADR-200 D6 — A RUN WAITING FOR A PERSON IS NEITHER "NOTHING" NOR "WORKING". Saying
  // "nothing in flight" over a parked run is how an unattended run sits unseen for a day.
  const waiting = r.waiting ?? 0
  if (waiting > 0 && r.unreadable === 0) {
    const rows = (r.rows ?? []).filter(row => row.waiting_for || isQueued(row) || ['blocked', 'triage', 'review'].includes(row.state))
    if (rows.some(row => !isParkedBrief(row))) {
      return waiting === 1 && rows.length === 1
        ? `Nothing is running. ${waitingLabel(rows[0])}. See below.`
        : `Nothing is running. ${waiting} items are waiting. See below.`
    }
    return `Nothing is running. ${waiting} run${waiting === 1 ? ' is' : 's are'} waiting for a person ` +
      `to confirm a brief — see below.`
  }
  if (r.unreadable > 0) {
    return `Nothing can be confirmed: ${r.unreadable} source${r.unreadable === 1 ? '' : 's'} could not be read. ` +
      `This is NOT "nothing is running" — see the rows below for what failed.`
  }
  if (r.not_configured > 0) {
    return `Nothing in flight. ${r.not_configured} source${r.not_configured === 1 ? '' : 's'} ` +
      `${r.not_configured === 1 ? 'is' : 'are'} not configured on this instance, so this view is partial.`
  }
  return 'Nothing in flight. Every source answered.'
}

/** An absolute URL must not be handed to the router, which would treat it as a path. */
export function isExternal(href: string): boolean {
  return /^https?:\/\//i.test(href)
}

function KindBadge({ kind }: { kind: string }) {
  const cls =
    kind === 'unreadable' ? 'bg-red-100 text-red-800'
      : kind === 'authoring' ? 'bg-indigo-100 text-indigo-800'
        : kind === 'investigation' ? 'bg-amber-100 text-amber-800'
          : kind === 'capture' ? 'bg-emerald-100 text-emerald-800'
            : 'bg-slate-100 text-slate-700'
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{kind}</span>
}

/** The words a waiting or queued row leads with. Pure, so the wording is testable. */
export function waitingLabel(row: Pick<InFlightRow, 'waiting_for' | 'unattended'> & Partial<Pick<InFlightRow, 'state'>>): string {
  if (isQueued(row)) return 'Queued, no worker has claimed it yet'
  if (row.state === 'triage') return 'Waiting for a person to recover it'
  if (row.state === 'review') return 'Waiting for a person to check its result'
  if (isParkedBrief(row)) {
    const what = row.waiting_for === 'brief_amendment'
      ? 'waiting for a person to amend the brief'
      : 'waiting for a person to confirm the brief'
    return row.unattended ? `Unattended run — ${what}` : what.charAt(0).toUpperCase() + what.slice(1)
  }
  if (!row.waiting_for && !row.state) return ''
  const labels: Record<string, string> = {
    needs_input: 'Waiting for a person to answer a question',
    capability: 'Waiting for a person, the agent lacks something it needs',
    transient: 'Waiting for a person, it hit a temporary failure',
    timed_out: 'Waiting for a person, its run timed out',
    remote_unknown: 'Waiting for a person to check what happened',
  }
  return labels[row.waiting_for ?? ''] ?? 'Waiting for a person'
}

/**
 * ADR-200 D6 — confirm a parked run's brief WITHOUT leaving this screen.
 *
 * An unattended run is keyed on Mission Control's machine identity, so it is in nobody's
 * editor: this panel is the one place a person can see its draft brief and act on it. It
 * reuses the rail's own ScopingCard, so the brief reads, and is decided, exactly as it does
 * in the editor — the same card, the same decision route, with `run_operator` naming the run.
 */
export function ParkedBriefPanel({ row, onDecided }: { row: InFlightRow; onDecided: () => void }) {
  const agent = row.run_agent ?? ''
  const workflow = row.run_workflow ?? ''
  const runOperator = row.run_operator
  const [run, setRun] = useState<AuthoringRunState | null>(null)
  const [cp, setCp] = useState<Checkpoint | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const r = await api.authoringRun(agent, workflow, runOperator)
      const got = (r.run as AuthoringRunState | null) ?? null
      setRun(got)
      setCp(r.pending_checkpoint
        ?? (got?.checkpoints ?? []).find(c => !c.decision || c.decision === 'pending')
        ?? null)
      setErr('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'could not read this run')
    }
  }, [agent, workflow, runOperator])
  useEffect(() => { void load() }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setErr('')
    try {
      await fn()
      await load()
      onDecided()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'the decision could not be recorded')
    } finally {
      setBusy(false)
    }
  }

  if (!run || !cp) {
    return <div className="text-xs text-slate-500">{err || 'Reading the brief…'}</div>
  }
  if (cp.reason !== 'scoping') {
    return <div className="text-xs text-slate-600">This run is no longer waiting on its brief.</div>
  }
  return (
    <ScopingCard
      key={run.brief_round ?? 0}
      cp={cp} run={run} busy={busy} error={err}
      onDecideBrief={(answers, confirm) =>
        void act(() => api.decideAuthoringBrief(agent, workflow, answers, confirm, runOperator))}
      onDecide={(decision, redirect, answer) =>
        void act(() => api.decideAuthoringCheckpoint(agent, workflow, decision, redirect, answer, runOperator))}
    />
  )
}

function CardControls({row,onChanged}:{row:InFlightRow;onChanged?:()=>void}) {
 const [answer,setAnswer]=useState('')
 const [answering,setAnswering]=useState(false)
 const [busy,setBusy]=useState(false)
 const [error,setError]=useState('')
 const act=async(action:string)=>{
  setBusy(true);setError('')
  try {
   await api.cardAction(row.card_id!,action,{
     board:row.board,answer,outcome:'cancelled',decision:'approve',operation_id:crypto.randomUUID()})
   setAnswering(false);onChanged?.()
  }catch(e){setError(e instanceof Error?e.message:'Recovery failed')}finally{setBusy(false)}
 }
 if(!row.can_control||row.case_owner)return null
 const labels:Record<string,string>={answer:'Answer',unblock:'Unblock',archive:'Archive',triage:'Recover triage',review:'Approve review',ack:'Acknowledge'}
 return <div>
  {(row.allowed_actions||[]).map(action=><button className="border rounded px-2 py-1 text-xs m-1" key={action} disabled={busy}
   onClick={()=>action==='answer'?setAnswering(true):void act(action)}>{labels[action]||action}</button>)}
  {answering&&<form onSubmit={e=>{e.preventDefault();void act('answer')}}>
   <label>Answer<textarea aria-label="Answer" value={answer} onChange={e=>setAnswer(e.target.value)}/></label>
   <button disabled={busy||!answer.trim()}>Send answer</button>
  </form>}
  {error&&<p role="alert">{error}</p>}
 </div>
}

function Row({ row, onChanged }: { row: InFlightRow; onChanged?: () => void }) {
  const stale = !!row.stale
  const waiting = !!row.waiting_for
  const [open, setOpen] = useState(false)
  return (
    <div className={`border rounded p-3 ${stale ? 'border-red-300 bg-red-50' : waiting ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
    <div className="flex items-start gap-3">
      <div className="w-28 shrink-0"><KindBadge kind={row.kind} /></div>

      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{row.subject}</div>
        {row.detail && <div className="text-xs text-slate-600 truncate">{row.detail}</div>}
        {/* An unreadable row must say WHY. A source that failed without a reason is
            the same non-answer as a spinner. */}
        {row.reason && <div className="text-xs text-red-700 mt-1">{row.reason}</div>}
        {/* A warning the backend computed and nothing displayed would be the
            invisible-capability failure this product keeps producing. The deploy
            overlap warning is the whole reason the deploy row exists. */}
        {row.warn && <div className="text-xs text-amber-800 mt-1">⚠ {row.warn}</div>}
        {waiting && (
          <div className="text-xs font-medium text-amber-900 mt-1" data-testid="waiting-label">
            {waitingLabel(row)}
          </div>
        )}
        {row.owner && !row.unattended && <div className="text-xs text-slate-500 mt-1">started by {row.owner}</div>}
      </div>

      {/* THE STATE IS THE PRIMARY FIELD, elapsed is secondary (D2). At 1016s
          "elapsed" cannot separate working from looping from stuck. */}
      <div className="w-40 shrink-0 text-right">
        <div className={`text-sm font-medium ${stale ? 'text-red-700' : 'text-slate-800'}`}>{row.state}</div>
        <div className="text-xs text-slate-500">
          {shortAge(row.age_s)}
          {typeof row.beat_age_s === 'number' && (
            <span className={stale ? 'text-red-600' : ''}> · beat {shortAge(row.beat_age_s)} ago</span>
          )}
        </div>
      </div>

      {/* EVERY ROW OFFERS A MOVE (D8). */}
      <div className="w-36 shrink-0 text-right">
        {/* A deploy links to GitHub, an authoring run to the editor. An absolute URL
            through <Link> would be routed as an in-app path and 404 — the router does
            not know it is external. */}
        {row.href
          ? isExternal(row.href)
            ? <a href={row.href} target="_blank" rel="noreferrer"
              className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100">
              {row.action || (row.case_owner==='clarification'?'Open Chat':row.case_owner==='investigation'?'Open Investigation':'open')}
            </a>
            : <Link to={row.href} className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100">
              {row.action || (row.case_owner==='clarification'?'Open Chat':row.case_owner==='investigation'?'Open Investigation':'open')}
            </Link>
          : <span className="text-xs text-slate-400">{row.action || '—'}</span>}
        {row.card_id && <CardControls row={row} onChanged={onChanged}/>}
        {/* ADR-200 D6 — THE MOVE FOR A PARKED RUN IS HERE, not a console. */}
        {waiting && row.decidable && row.run_agent && row.run_workflow && (
          <button type="button" onClick={() => setOpen(o => !o)}
            className="mt-1 block w-full text-xs px-2 py-1 rounded border border-amber-400 bg-white hover:bg-amber-100">
            {open ? 'Hide brief' : 'Review brief'}
          </button>
        )}
      </div>
    </div>
    {open && waiting && row.decidable && (
      <div className="mt-2">
        <ParkedBriefPanel row={row} onDecided={() => onChanged?.()} />
      </div>
    )}
    </div>
  )
}

export function InFlight() {
  const [res, setRes] = useState<InFlightResult | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const notifyAttention = useAttentionNotify()
  const parkedSeenRef = useRef<Set<string> | null>(null)

  const load = useCallback(async () => {
    try {
      const next = await api.fleetInFlight()
      const reconciliation = newlyParkedRows(parkedSeenRef.current, next)
      parkedSeenRef.current = reconciliation.seen
      for (const row of reconciliation.alerts) {
        notifyAttention({
          id: `in-flight-scoping:${parkedRowKey(row)}:${next.observed_at}`,
          source: 'in-flight-scoping',
          reason: 'input-needed',
        })
      }
      for (const row of next.rows.filter(r=>r.card_id&&r.stale)) {
        notifyAttention({id:'waiting-card:'+row.card_id+':'+row.waiting_event_id,source:'in-flight-scoping',reason:'input-needed'})
      }
      setRes(next)
      setErr('')
    } catch (e) {
      // The ENDPOINT failing is different from a source failing, and is reported as
      // such: this screen cannot claim the fleet is idle when it could not ask.
      setErr(e instanceof Error ? e.message : 'could not read in-flight state')
    } finally {
      setLoading(false)
    }
  }, [notifyAttention])

  useEffect(() => { void load() }, [load])

  // A hidden In Flight tab continues reading every five seconds. Phase gaps and unreadable
  // sources do not prove a watched run finished, so stopping would miss a later parked brief.
  useEffect(() => {
    const t = setInterval(() => {
      void load()
    }, 5000)
    return () => clearInterval(t)
  }, [load])

  const empty = res ? emptyStateSentence(res) : ''
  const queued = res?.rows.filter(row => row.kind === 'card' && isQueued(row)).length ?? 0
  const waitingForPerson = Math.max(0, (res?.waiting ?? 0) - queued)

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">In flight</h1>
        {res && (
          <div className="text-xs text-slate-500">
            {res.working} working
            {waitingForPerson > 0 && <span className="text-amber-700"> · {waitingForPerson} waiting for a person</span>}
            {queued > 0 && <span> · {queued} queued</span>}
            {res.unreadable > 0 && <span className="text-red-600"> · {res.unreadable} unreadable</span>}
            {res.not_configured > 0 && <span> · {res.not_configured} not configured</span>}
            {' · '}stale after {res.stale_after_s}s
          </div>
        )}
      </div>

      {loading && !res && <p className="text-sm text-slate-500">Reading…</p>}

      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>Could not read this fleet&apos;s in-flight state.</strong> {err}
          <div className="mt-2">
            <button type="button" onClick={() => void load()}
              className="text-xs px-2 py-1 rounded bg-white border border-red-300 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {res && empty && (
        <p className={`text-sm ${res.unreadable > 0 ? 'text-red-700' : 'text-slate-600'}`}>{empty}</p>
      )}

      {res && res.rows.length > 0 && (
        <div className="space-y-2">
          {res.rows.map((r, i) => <Row key={`${r.kind}-${r.subject}-${r.run_operator ?? ''}-${i}`} row={r} onChanged={() => void load()} />)}
        </div>
      )}
    </div>
  )
}
